import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { AudioState, RadioStation, TrackInfo, SleepTimer, UserSettings } from '@/types';
import { EQ_BANDS, EQ_DEFAULT_GAIN } from '@/constants/equalizer';
import { DEFAULT_LIVE_STATION, isLegacyTestStation } from '@/constants/stations';

interface AudioStore extends AudioState {
  // Actions
  setPlaying: (isPlaying: boolean) => void;
  setBuffering: (isBuffering: boolean) => void;
  setVolume: (volume: number) => void;
  setCurrentStation: (station: RadioStation | null) => void;
  setCurrentTrack: (track: TrackInfo | null) => void;
  setError: (error: string | null) => void;
  togglePlay: () => void;
  
  // Favorites
  favorites: string[];
  addFavorite: (stationId: string) => void;
  removeFavorite: (stationId: string) => void;
  isFavorite: (stationId: string) => boolean;
  
  // Sleep Timer
  sleepTimer: SleepTimer;
  setSleepTimer: (minutes: number) => void;
  clearSleepTimer: () => void;
  decrementTimer: () => void;
  
  // Equalizer (5 bandi)
  eqBands: number[];
  setEqBand: (index: number, gain: number) => void;
  resetEq: () => void;
  
  // Settings
  settings: UserSettings;
  updateSettings: (settings: Partial<UserSettings>) => void;
}

const initialState: AudioState = {
  isPlaying: false,
  isBuffering: false,
  volume: 0.8,
  currentStation: null,
  currentTrack: null,
  error: null,
};

const initialSleepTimer: SleepTimer = {
  isActive: false,
  remainingSeconds: 0,
  totalSeconds: 0,
};

const initialSettings: UserSettings = {
  autoPlay: true,
  defaultTimer: null,
  audioQuality: 'high',
  normalizeVolume: false,
  crossfade: false,
  notifications: true,
};

export const useAudioStore = create<AudioStore>()(
  persist(
    (set, get) => ({
      ...initialState,
      favorites: [],
      sleepTimer: initialSleepTimer,
      settings: initialSettings,

      setPlaying: (isPlaying) => set({ isPlaying }),
      setBuffering: (isBuffering) => set({ isBuffering }),
      setVolume: (volume) => set({ volume }),
      setCurrentStation: (station) => set({ currentStation: station }),
      setCurrentTrack: (track) => set({ currentTrack: track }),
      setError: (error) => set({ error }),
      
      togglePlay: () => set((state) => ({ isPlaying: !state.isPlaying })),

      addFavorite: (stationId) =>
        set((state) => ({
          favorites: [...state.favorites, stationId],
        })),

      removeFavorite: (stationId) =>
        set((state) => ({
          favorites: state.favorites.filter((id) => id !== stationId),
        })),

      isFavorite: (stationId) => get().favorites.includes(stationId),

      setSleepTimer: (minutes) =>
        set({
          sleepTimer: {
            isActive: true,
            remainingSeconds: minutes * 60,
            totalSeconds: minutes * 60,
          },
        }),

      clearSleepTimer: () =>
        set({
          sleepTimer: initialSleepTimer,
        }),

      decrementTimer: () =>
        set((state) => ({
          sleepTimer: {
            ...state.sleepTimer,
            remainingSeconds: Math.max(0, state.sleepTimer.remainingSeconds - 1),
          },
        })),

      eqBands: Array(EQ_BANDS.length).fill(EQ_DEFAULT_GAIN),
      setEqBand: (index, gain) =>
        set((state) => {
          const next = [...state.eqBands];
          if (index >= 0 && index < next.length) next[index] = gain;
          return { eqBands: next };
        }),
      resetEq: () =>
        set({ eqBands: Array(EQ_BANDS.length).fill(EQ_DEFAULT_GAIN) }),

      updateSettings: (newSettings) =>
        set((state) => ({
          settings: { ...state.settings, ...newSettings },
        })),
    }),
    {
      name: 'suton-radio-storage',
      partialize: (state) => ({
        favorites: state.favorites,
        settings: state.settings,
        volume: state.volume,
        eqBands: state.eqBands,
        currentStation: state.currentStation,
      }),
      merge: (persisted, current) => {
        const p = persisted as Record<string, unknown>;
        const stored = p?.eqBands;
        const eqBands =
          Array.isArray(stored) &&
          stored.length === EQ_BANDS.length &&
          stored.every((v) => typeof v === 'number')
            ? stored as number[]
            : Array(EQ_BANDS.length).fill(EQ_DEFAULT_GAIN);

        // Migracija: testne/legacy stream URL-ove (zeno, streamupsolutions, …)
        // resetuj na default SUTON Live — inače HomeScreen match nikad ne pogodi.
        let currentStation = (p?.currentStation as RadioStation | null | undefined) ?? null;
        if (isLegacyTestStation(currentStation)) {
          currentStation = DEFAULT_LIVE_STATION;
        }

        return { ...current, ...p, eqBands, currentStation };
      },
    }
  )
);
